import { type AiPlatformId } from '@bmai/licenses-api-client';

/** Map a backend `providerTypeId` (e.g. "anthropic/console-admin@1.0.0") to its AI platform id. */
export function platformIdForProviderType(providerTypeId: string): AiPlatformId | null {
    if (providerTypeId.startsWith('anthropic/')) return 'claude';
    if (providerTypeId.startsWith('openai/')) return 'chatgpt';
    if (providerTypeId.startsWith('microsoft-copilot/')) return 'microsoft-copilot';
    if (providerTypeId.startsWith('github-copilot/')) return 'github-copilot';
    if (providerTypeId.startsWith('google-vault/') || providerTypeId.startsWith('google-workspace/')) return 'gemini';
    if (providerTypeId.startsWith('aws-bedrock/')) return 'bedrock';
    if (providerTypeId.startsWith('gcp-vertex/')) return 'vertex';
    if (providerTypeId.startsWith('xai/')) return 'grok';
    if (providerTypeId.startsWith('azure-ai-foundry/')) return 'azure-ai-foundry';
    return null;
}

const PLATFORM_LABELS: Record<AiPlatformId, string> = {
    claude: 'Claude',
    chatgpt: 'ChatGPT',
    'github-copilot': 'GitHub Copilot',
    'microsoft-copilot': 'Microsoft Copilot',
    gemini: 'Gemini',
    bedrock: 'Amazon Bedrock',
    vertex: 'Google Vertex AI',
    grok: 'Grok',
    'azure-ai-foundry': 'Azure AI Foundry',
};

/** Friendly display label for an AI platform id. */
export function platformLabel(platformId: AiPlatformId): string {
    return PLATFORM_LABELS[platformId];
}

/** Platforms billed purely by consumption (no seats), so they carry no license
 * pools and are excluded from the seat-management surfaces. */
const USAGE_ONLY_PLATFORMS: ReadonlySet<AiPlatformId> = new Set<AiPlatformId>(['bedrock', 'vertex', 'grok', 'azure-ai-foundry']);

/** Whether a provider type is seat-licensed. Usage-only platforms — Amazon Bedrock,
 * Google Vertex AI, Grok, and Azure AI Foundry, which are consumption-priced with no
 * seats — return false, so the licenses management surfaces omit them. Unknown types
 * default to true. */
export function isSeatLicensedProviderType(providerTypeId: string): boolean {
    const platformId = platformIdForProviderType(providerTypeId);
    return platformId === null || !USAGE_ONLY_PLATFORMS.has(platformId);
}
